import React, { useEffect } from "react"
import {
  Col,
  Row,
  Button,
  TabPane,
  Form,
  FormGroup,
  Label,
  Input,
  Card,
  CardBody,
} from "reactstrap"

const CPSRequestDataTab = ({ nextTab, previousTab, request, setRequest }) => {
  const handleInputChange = (field, value) => {
    // Prevent changes to locked fields for sub-requests
    if (
      request.isSubRequest &&
      (field === "needType" ||
        field === "safetyStockMin" ||
        field === "safetyStockMax")
    ) {
      return
    }
    setRequest({ ...request, [field]: value })
  }

  // Reset supply form when CPS type changes since available options differ
  useEffect(() => {
    if (request.type && request.supplyForm) {
      const programmingOptions = ["tray", "reel", "tube"]
      const sleevingOptions = ["tray", "tube", "bulk"]

      const isValidForType =
        request.type === "Programming"
          ? programmingOptions.includes(request.supplyForm)
          : request.type === "Sleeving"
          ? sleevingOptions.includes(request.supplyForm)
          : true

      if (!isValidForType) {
        setRequest({ ...request, supplyForm: "" })
      }
    }
  }, [request.type])

  const canProceed =
    request.articleDescription &&
    request.needType &&
    request.quantity &&
    request.mpn &&
    request.manufacturer &&
    request.packageType &&
    request.deliveryForm &&
    request.supplyForm

  return (
    <TabPane tabId={2}>
      <div>
        <div className="text-center mb-4">
          <h5 className="card-title">Request Information</h5>
          <p className="card-title-desc">
            Provide details about your {request.type} requirements
          </p>
          {request.isSubRequest && (
            <div className="alert alert-info">
              <i className="bx bx-info-circle me-2"></i>
              This is a sub-request for parent request{" "}
              <strong>{request.parentRequestNumber}</strong>. Need type is
              locked to <strong>One-time requirement</strong>.
            </div>
          )}
        </div>

        <Row className="justify-content-center">
          <Col lg="8">
            <Card>
              <CardBody>
                <Form>
                  <FormGroup className="mb-3">
                    <Label htmlFor="articleDescription">
                      Article Description *
                    </Label>
                    <Input
                      type="textarea"
                      id="articleDescription"
                      placeholder="Describe the PCB/component that needs programming or sleeving..."
                      value={request.articleDescription}
                      onChange={e =>
                        handleInputChange("articleDescription", e.target.value)
                      }
                      rows="3"
                    />
                  </FormGroup>

                  <FormGroup className="mb-3">
                    <Label htmlFor="customerOrderNumber">
                      Customer Order Number
                    </Label>
                    <Input
                      type="text"
                      id="customerOrderNumber"
                      placeholder="Your internal order number (optional)"
                      value={request.customerOrderNumber}
                      onChange={e =>
                        handleInputChange("customerOrderNumber", e.target.value)
                      }
                    />
                    <div className="form-text text-muted">
                      Optional: Your internal order number for reference
                    </div>
                  </FormGroup>

                  {/* Article Details Section */}
                  <div className="bg-light p-3 rounded mb-3">
                    <h6 className="mb-3">Article Details *</h6>
                    <p className="text-muted small mb-3">
                      Please provide the required component information
                    </p>

                    <Row>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="mpn">
                            MPN (Manufacturer Part Number) *
                          </Label>
                          <Input
                            type="text"
                            id="mpn"
                            placeholder="Part number"
                            value={request.mpn}
                            onChange={e =>
                              handleInputChange("mpn", e.target.value)
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="manufacturer">Manufacturer *</Label>
                          <Input
                            type="text"
                            id="manufacturer"
                            placeholder="Component manufacturer"
                            value={request.manufacturer}
                            onChange={e =>
                              handleInputChange("manufacturer", e.target.value)
                            }
                          />
                        </FormGroup>
                      </Col>
                    </Row>

                    <Row>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="packageType">Package Type *</Label>
                          <Input
                            type="text"
                            id="packageType"
                            placeholder="e.g., QFP, SOIC, BGA"
                            value={request.packageType}
                            onChange={e =>
                              handleInputChange("packageType", e.target.value)
                            }
                          />
                        </FormGroup>
                      </Col>
                      <Col md="6">
                        <FormGroup className="mb-3">
                          <Label htmlFor="deliveryForm">Delivery Form *</Label>
                          <Input
                            type="select"
                            id="deliveryForm"
                            value={request.deliveryForm}
                            onChange={e =>
                              handleInputChange("deliveryForm", e.target.value)
                            }
                          >
                            <option value="">Select delivery form...</option>
                            <option value="tray">Tray</option>
                            <option value="reel">Reel</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>

                    <FormGroup className="mb-3">
                      <Label htmlFor="supplyForm">Supply Form *</Label>
                      <Input
                        type="select"
                        id="supplyForm"
                        value={request.supplyForm}
                        onChange={e =>
                          handleInputChange("supplyForm", e.target.value)
                        }
                      >
                        <option value="">Select supply form...</option>
                        {request.type === "Programming" ? (
                          <>
                            <option value="tray">Tray</option>
                            <option value="reel">Reel</option>
                            <option value="tube">Tube</option>
                          </>
                        ) : request.type === "Sleeving" ? (
                          <>
                            <option value="tray">Tray</option>
                            <option value="tube">Tube</option>
                            <option value="bulk">Bulk</option>
                          </>
                        ) : (
                          <>
                            <option value="tray">Tray</option>
                            <option value="reel">Reel</option>
                            <option value="tube">Tube</option>
                            <option value="bulk">Bulk</option>
                            <option value="tray-and-reel">Tray & Reel</option>
                            <option value="other">Other</option>
                          </>
                        )}
                      </Input>
                    </FormGroup>
                  </div>

                  <Row>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="needType">Need Type *</Label>
                        <Input
                          type="select"
                          id="needType"
                          value={request.needType}
                          onChange={e =>
                            handleInputChange("needType", e.target.value)
                          }
                          disabled={request.isSubRequest}
                          className={request.isSubRequest ? "bg-light" : ""}
                        >
                          <option value="">Select need type...</option>
                          <option value="one-time">One-time requirement</option>
                          <option value="annual">Annual quantity</option>
                        </Input>
                        {request.isSubRequest && (
                          <div className="form-text text-muted">
                            <i className="bx bx-lock me-1"></i>
                            Locked to "One-time requirement" for sub-requests
                          </div>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup className="mb-3">
                        <Label htmlFor="quantity">Quantity *</Label>
                        <Input
                          type="number"
                          id="quantity"
                          placeholder="Enter quantity"
                          value={request.quantity}
                          onChange={e =>
                            handleInputChange("quantity", e.target.value)
                          }
                          min="1"
                        />
                      </FormGroup>
                    </Col>
                  </Row>

                  {/* Safety Stock Settings - Only show for annual requirements and not for sub-requests */}
                  {request.needType === "annual" && !request.isSubRequest && (
                    <div className="bg-light p-3 rounded mb-3">
                      <h6 className="mb-3">Safety Stock Settings</h6>
                      <p className="text-muted small mb-3">
                        Define minimum and maximum stock levels (can be adjusted
                        anytime by customer)
                      </p>
                      <Row>
                        <Col md="6">
                          <FormGroup className="mb-2">
                            <Label htmlFor="safetyStockMin">
                              Minimum Stock
                            </Label>
                            <Input
                              type="number"
                              id="safetyStockMin"
                              placeholder="Min quantity"
                              value={request.safetyStockMin}
                              onChange={e =>
                                handleInputChange(
                                  "safetyStockMin",
                                  e.target.value
                                )
                              }
                              min="0"
                            />
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup className="mb-2">
                            <Label htmlFor="safetyStockMax">
                              Maximum Stock
                            </Label>
                            <Input
                              type="number"
                              id="safetyStockMax"
                              placeholder="Max quantity"
                              value={request.safetyStockMax}
                              onChange={e =>
                                handleInputChange(
                                  "safetyStockMax",
                                  e.target.value
                                )
                              }
                              min="0"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </div>
                  )}
                </Form>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

      <div className="d-flex justify-content-between mt-4">
        <Button color="secondary" onClick={previousTab}>
          Previous
        </Button>
        <Button
          color="primary"
          onClick={() => nextTab(1)}
          disabled={!canProceed}
        >
          Next - Sample Requirements
        </Button>
      </div>
    </TabPane>
  )
}

export default CPSRequestDataTab
